/**
 * Project calendar — a shared, all-roles month/week/agenda view that plots each
 * project as a single event on its scheduled window, colored by status.
 *
 *   GET /projects/calendar?from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * One event per project (not per task): high-level "what's happening, where,
 * and in what state". Unapproved requests are excluded (nothing to plot yet).
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PickupStatusSchema, PrioritySchema, ProjectKindSchema, ProjectStatusSchema, TaskStatusSchema, TaskTypeSchema, } from './enums.js';
const DayStringSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const ProjectCalendarQuerySchema = z.object({
    from: DayStringSchema.describe('inclusive range start (business day)'),
    to: DayStringSchema.describe('inclusive range end (business day)'),
});
/** Which dates positioned this project on the calendar (best available signal). */
export const CalendarDateSourceSchema = z.enum(['scheduled', 'requested', 'deadline']);
/** A crew member assigned to the project, lean for an avatar on the bar. */
export const CalendarCrewSchema = z.object({
    display_name: z.string(),
    avatar_url: z.string().url().nullable(),
});
export const ProjectCalendarItemSchema = z.object({
    id: UuidSchema,
    title: z.string(),
    client_name: z.string(),
    status: ProjectStatusSchema,
    kind: ProjectKindSchema,
    priority: PrioritySchema,
    /** Inclusive span (business-day strings) — the frontend renders all-day bars. */
    start_date: DayStringSchema,
    end_date: DayStringSchema,
    date_source: CalendarDateSourceSchema,
    /** Short "City, ST" (falls back to street line); null when no address. */
    location: z.string().nullable(),
    /** Scheduled tasks under this project (0 when positioned by requested/deadline). */
    task_count: z.number().int().nonnegative(),
    /** Distinct crew assigned across the project's tasks — rendered as avatars on
     *  the bar (esp. in week/day). Empty until crew is assigned. */
    crew: z.array(CalendarCrewSchema),
    /**
     * Business-local time range ("7:00 AM – 5:30 PM") when crew IS scheduled and
     * the work falls on a single day. Null when not yet scheduled (hours unknown)
     * or for multi-day spans (a cross-day range would mislead). The calendar never
     * invents hours — `date_source === 'scheduled'` means committed/known.
     */
    time_label: z.string().nullable(),
});
export const ProjectCalendarResponseSchema = z.object({
    from: DayStringSchema,
    to: DayStringSchema,
    items: z.array(ProjectCalendarItemSchema),
});
// ----------------------------------------------------------------------------
// Timed feed — the week/day grid. Unlike the month feed (one all-day bar per
// project), this returns task-grain TIMED blocks plus a project's pending
// request as its own dashed block(s), so week/day shows the actual work.
// ----------------------------------------------------------------------------
/** A committed (or PM-pending) unit of work, plotted at its scheduled hours. */
export const TaskCalendarBlockSchema = z.object({
    kind: z.literal('task'),
    /** The task id. */
    id: UuidSchema,
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string(),
    title: z.string(),
    type: TaskTypeSchema,
    status: TaskStatusSchema,
    scheduled_start: IsoDateTimeSchema,
    scheduled_end: IsoDateTimeSchema.nullable(),
    crew: z.array(CalendarCrewSchema),
    /** Crew the task needs — shown as "N of needed" in the quick-assign dialog. */
    crew_size_needed: z.number().int().nonnegative(),
    /** PM-requested task awaiting supervisor confirmation (Stage 4b) — rendered
     *  dashed/faded. Always false until 4b lands the column. */
    pending: z.boolean(),
    /** The requester's note on a pending task — shown on hover. */
    request_note: z.string().nullable(),
});
/** A pending project request, one block per requested day (dashed/faded). */
export const RequestCalendarBlockSchema = z.object({
    kind: z.literal('request'),
    /** Synthetic id: `<project_id>:<YYYY-MM-DD>` (one block per requested day). */
    id: z.string(),
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string(),
    start: IsoDateTimeSchema,
    end: IsoDateTimeSchema,
});
/** A PICKUP (workstream C) plotted at its scheduled window — one block per run. */
export const PickupCalendarBlockSchema = z.object({
    kind: z.literal('pickup'),
    /** The pickup id. */
    id: UuidSchema,
    title: z.string(),
    status: PickupStatusSchema,
    scheduled_start: IsoDateTimeSchema,
    scheduled_end: IsoDateTimeSchema.nullable(),
    crew: z.array(CalendarCrewSchema),
    /** How many projects' orders this run covers. */
    project_count: z.number().int().nonnegative(),
});
export const CalendarBlockSchema = z.discriminatedUnion('kind', [
    TaskCalendarBlockSchema,
    RequestCalendarBlockSchema,
    PickupCalendarBlockSchema,
]);
export const CalendarTimedResponseSchema = z.object({
    from: DayStringSchema,
    to: DayStringSchema,
    /** Active crew on the team — the denominator for a task block's assigned-of-team
     *  avatar slots (filled = assigned, the rest render as empty placeholder rings). */
    crew_total: z.number().int().nonnegative(),
    blocks: z.array(CalendarBlockSchema),
});
//# sourceMappingURL=calendar.js.map